import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addHours, startOfHour } from 'date-fns';
import * as Yup from 'yup';

import {
  createAppointment,
  fetchServices,
  updateAppointment,
} from '../../models/appointment/appointment';
import { fetchLocations } from '../../models/location/location';
import { fetchUserQueryKey, fetchUsers } from '../../models/user/user';
import toast from '../../utils/toast';

import type { Appointment } from '../../models/appointment/_types';

export const useEditAppointmentDialog = ({ appointment, onClose }) => {
  const queryClient = useQueryClient();

  const { data: services, isLoading: servicesLoading } = useQuery({
    queryKey: ['services'],

    queryFn: () => {
      return fetchServices({ includePrivate: true });
    },
  });

  const {
    data: trainers,
    isLoading: trainersLoading,
    error: trainersError,
  } = useQuery({
    queryKey: fetchUserQueryKey({ fetchTrainers: true }),

    queryFn: () => {
      return fetchUsers({ fetchTrainers: true });
    },
  });

  const {
    data: locations,
    isLoading: locationsLoading,
    error: locationsError,
  } = useQuery({
    queryKey: ['locations'],

    queryFn: () => {
      return fetchLocations();
    },
  });

  const formik = {
    initialValues: {
      serviceId: appointment?.serviceId || '',
      locationId: appointment?.locationId || '',
      primaryTrainerIds: appointment?.primaryTrainerIds || undefined,
      secondaryTrainerIds: appointment?.secondaryTrainerIds || undefined,
      startTime: appointment?.startTime || startOfHour(addHours(new Date(), 1)),
      endTime: appointment?.endTime || startOfHour(addHours(new Date(), 2)),
    },
    validationSchema: Yup.object({
      serviceId: Yup.number().required('Service is required'),
      locationId: Yup.number(),
      primaryTrainerIds: Yup.array().of(
        Yup.number().required('Primary trainer is required')
      ),
      secondaryTrainerIds: Yup.array().of(Yup.number()),
      startTime: Yup.date()
        .min(new Date(), 'Appointment must be in the future')
        .required('Required'),
      endTime: Yup.date()
        .min(Yup.ref('startTime'), 'End date must be later than start date')
        .required('Required'),
      description: Yup.string(),
      notes: Yup.string(),
    }),
    onSubmit: async (values, actions) => {
      const submission = await mutateAsync({ ...values, id: appointment?.id });
      actions.resetForm();
      onClose();
      return submission;
    },
  };

  const createAppointmentMutation = useMutation({
    mutationFn: (appointment: Partial<Appointment>) => {
      return createAppointment(appointment);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Event successfully created');
    },
    onError: () => {
      toast.error('Event could not be created');
    },
  });

  const updateAppointmentMutation = useMutation({
    mutationFn: (appointment: Appointment) => {
      return updateAppointment(appointment);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Event successfully updated');
    },
    onError: () => {
      toast.error('Event could not be updated');
    },
  });

  const { mutateAsync } = appointment
    ? updateAppointmentMutation
    : createAppointmentMutation;

  const getTrainerFromId = (id) => {
    return (trainers || []).find((t) => t.id === id);
  };

  const getTrainerOptions = (currentTrainers) => {
    return trainers?.filter((t) => !currentTrainers?.includes(t.id)) || [];
  };

  return {
    services,
    servicesLoading,
    trainers,
    trainersLoading,
    trainersError,
    locations,
    locationsLoading,
    locationsError,
    formik,
    getTrainerFromId,
    getTrainerOptions,
  };
};
